import {
  ToastProvider,
  type ToastPosition,
  toastManager,
} from "@salestrak/ui";
import React from "react";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";

/** Anchor used when no position is passed / no user preference is stored. */
export const DEFAULT_TOAST_POSITION: ToastPosition = "bottom-left";

/**
 * The six anchors with display labels — render these as the choices anywhere
 * the position is user-configurable (the preferences screen's Select). This
 * file is their canonical home: it owns the viewport that honors them.
 */
export const TOAST_POSITION_OPTIONS: ReadonlyArray<{
  label: string;
  value: ToastPosition;
}> = [
  { label: "Top left", value: "top-left" },
  { label: "Top center", value: "top-center" },
  { label: "Top right", value: "top-right" },
  { label: "Bottom left", value: "bottom-left" },
  { label: "Bottom center", value: "bottom-center" },
  { label: "Bottom right", value: "bottom-right" },
];

/**
 * The one preference change that toasts: picking a position raises a toast AT
 * that position, so the choice is seen where it lands instead of read off a
 * Select. Every other preference saves silently.
 */
export function previewToastPosition(position: ToastPosition): void {
  const label =
    TOAST_POSITION_OPTIONS.find((o) => o.value === position)?.label ??
    position;
  toastManager.add({
    description: `Preview of the ${label.toLowerCase()} position`,
    title: "Toasts appear here",
    type: "info",
  });
}

const VALID_POSITIONS: ReadonlySet<string> = new Set(
  TOAST_POSITION_OPTIONS.map((o) => o.value),
);

/**
 * Resolves a stored position preference to a usable anchor. Preferences
 * travel as untyped strings (null while unloaded, arbitrary if the stored
 * row was ever hand-edited), so anything outside the six anchors falls back
 * to the default.
 */
export function resolveToastPosition(
  pref: string | null | undefined,
): ToastPosition {
  return pref && VALID_POSITIONS.has(pref)
    ? (pref as ToastPosition)
    : DEFAULT_TOAST_POSITION;
}

/**
 * App-level toast viewport: drives the library `ToastProvider` from the user's
 * toastPosition preference. Must render inside AppProviders (needs Apollo);
 * falls back to the default anchor while logged out / loading / on an invalid
 * stored value. ONE provider, ONE viewport — never mount a second
 * `ToastProvider`, or `toastManager` toasts render twice.
 *
 * Positions anchor to the shell's islands (`region="app-shell-islands"`),
 * not the viewport: the region runs from the page card's left edge to the
 * last docked island's right edge. A left-anchored toast opens inside the
 * page, clear of the module rail; right and center anchors measure from the
 * islands' outer edge, so a toast opens over an open profile island
 * instead of being squeezed beside it. Logged-out routes mount no shell,
 * where the region IS the viewport.
 */
export function AppToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { preferences } = useUserSettings();
  return (
    <ToastProvider
      position={resolveToastPosition(preferences?.toastPosition)}
      region="app-shell-islands"
    >
      {children}
    </ToastProvider>
  );
}
